import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { HOME } from "@/constants/testIds";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await register(name, email, password);
      toast.success("Welcome to Evolonef.");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed.");
    } finally { setBusy(false); }
  };

  return (
    <section className="mx-auto max-w-[1200px] px-5 md:px-10 py-14 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
      <div className="md:col-span-6 md:pr-10">
        <div className="eyebrow">The Portal</div>
        <h1 className="font-serif-ev mt-4 text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
          Begin your <span className="italic" style={{ color: "var(--ev-blue-2)" }}>return</span>.
        </h1>
        <p className="mt-6 text-[15px] max-w-md" style={{ color: "var(--ev-muted)" }}>
          Create a private account. Track sessions, receive journal prompts and hold your practice in one place.
        </p>
      </div>
      <form onSubmit={submit} className="md:col-span-6 md:col-start-7 ev-card p-7 md:p-10">
        <div className="eyebrow">Create account</div>
        <div className="font-serif-ev text-3xl mt-1">A quiet beginning.</div>
        <div className="mt-7 space-y-5">
          <div>
            <label className="text-xs uppercase tracking-widest" style={{ color: "var(--ev-muted)" }}>Name</label>
            <input data-testid={HOME.registerName} required value={name}
                   onChange={(e) => setName(e.target.value)}
                   className="mt-2 w-full bg-white border hairline rounded-full py-3 px-4 focus:outline-none focus:border-[color:var(--ev-blue-2)] text-[15px]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest" style={{ color: "var(--ev-muted)" }}>Email</label>
            <input data-testid={HOME.registerEmail} required type="email" value={email}
                   onChange={(e) => setEmail(e.target.value)}
                   className="mt-2 w-full bg-white border hairline rounded-full py-3 px-4 focus:outline-none focus:border-[color:var(--ev-blue-2)] text-[15px]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest" style={{ color: "var(--ev-muted)" }}>Password</label>
            <input data-testid={HOME.registerPassword} required type="password" minLength={6} value={password}
                   onChange={(e) => setPassword(e.target.value)}
                   className="mt-2 w-full bg-white border hairline rounded-full py-3 px-4 focus:outline-none focus:border-[color:var(--ev-blue-2)] text-[15px]" />
            <div className="text-xs mt-2" style={{ color: "var(--ev-muted)" }}>Minimum 6 characters.</div>
          </div>
        </div>
        <button data-testid={HOME.registerSubmit} disabled={busy} type="submit"
                className="mt-8 ev-btn ev-btn-primary w-full disabled:opacity-60">
          {busy ? "Creating…" : "Create account"} <ArrowRight className="w-4 h-4" />
        </button>
        <div className="mt-6 text-sm" style={{ color: "var(--ev-muted)" }}>
          Already a client? <Link to="/login" className="underline" style={{ color: "var(--ev-ink)" }}>Sign in</Link>
        </div>
      </form>
    </section>
  );
}
